import React, { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Alert,
  AlertTitle,
  Paper,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  VpnKey as VpnKeyIcon,
  Security as SecurityIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  Login as LoginIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import { useMsal } from '@azure/msal-react'
import {
  generateCertificate,
  createAppRegistration,
  verifyAdminConsent,
  verifyMSPProvisionerConsent,
  uploadToKeyVault,
  testCertificateConnection,
  getCertificateStatus,
  type CertificateGenerationResult,
} from '../services/api'

interface CertificateWizardProps {
  tenantId: string
  tenantDomain: string
  onComplete: () => void
}

const MSP_PLAN_IDS = ['msp-client', 'msp-client-dev']

const BASE_STEPS = [
  'Sign in to Microsoft 365',
  'Generate Certificate',
  'Create App Registration',
  'Upload to Key Vault',
  'Test Connection',
]

const CertificateWizard: React.FC<CertificateWizardProps> = ({
  tenantId,
  tenantId: _tenantId, // Keep for backward compatibility but use authenticatedTenantId from sign-in (delete?)
  tenantDomain,
  onComplete,
}) => {
  const { instance } = useMsal()
  const [searchParams] = useSearchParams()

  // Detect MSP Client plan from URL query param (?plan=msp-client-dev)
  const isMspPlan = MSP_PLAN_IDS.includes(searchParams.get('plan') ?? '')

  // Build the ordered step list dynamically based on plan type
  const steps = isMspPlan
    ? [...BASE_STEPS.slice(0, 3), 'MSP Platform Access', ...BASE_STEPS.slice(3)]
    : BASE_STEPS

  // Step indices shift by one for MSP plans due to the extra consent step at index 3
  const UPLOAD_STEP  = isMspPlan ? 4 : 3
  const SUCCESS_STEP = isMspPlan ? 6 : 5

  const [activeStep, setActiveStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  // Certificate data from Step 1
  const [certificateData, setCertificateData] = useState<CertificateGenerationResult | null>(null)

  // App registration data from Step 2 - authenticated user info
  const [adminToken, setAdminToken] = useState('')
  const [authenticatedTenantId, setAuthenticatedTenantId] = useState('')
  const [authenticatedTenantDomain, setAuthenticatedTenantDomain] = useState('')
  const [authenticatedUserName, setAuthenticatedUserName] = useState('')
  const [appId, setAppId] = useState<string | null>(null)

  // Admin consent URL from Step 3
  const [consentUrl, setConsentUrl] = useState<string | null>(null)
  const [consentPageOpened, setConsentPageOpened] = useState(false)
  const [consentCompleted, setConsentCompleted] = useState(false)
  const [consentCallbackUtc, setConsentCallbackUtc] = useState<string | null>(null)

  // MSP Platform Access step state (MSP plans only)
  const [mspProvisionerAppId, setMspProvisionerAppId] = useState<string | null>(null)
  const [mspRoleTargetAppId, setMspRoleTargetAppId] = useState<string | null>(null)
  const [mspProvisionerConsented, setMspProvisionerConsented] = useState(false)
  const [mspRoleTargetConsented, setMspRoleTargetConsented] = useState(false)
  const [mspConsentMetadataLoaded, setMspConsentMetadataLoaded] = useState(false)
  const [awaitingMspConsentReturn, setAwaitingMspConsentReturn] = useState(false)

  // Listen for admin consent completion message from the consent popup
  React.useEffect(() => {
    const handleConsentMessage = (payload: { adminConsent?: boolean; state?: string | null }) => {
      if (!payload.adminConsent) {
        return
      }

      if (payload.state === 'customer-app-registration-consent') {
        const callbackUtc = localStorage.getItem('tc_admin_consent_callback_utc') || new Date().toISOString()
        setConsentCallbackUtc(callbackUtc)
        setConsentCompleted(true)
        setConsentPageOpened(true)
      }

      if (payload.state === 'msp-provisioner-consent') {
        setMspProvisionerConsented(true)
      }

      if (payload.state === 'msp-role-target-consent') {
        setMspRoleTargetConsented(true)
      }
    }

    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; adminConsent?: boolean; state?: string | null } | null
      if (!data || data.type !== 'admin-consent-complete') {
        return
      }

      handleConsentMessage(data)
    }

    const storedConsent = localStorage.getItem('tc_admin_consent')
    if (storedConsent) {
      try {
        handleConsentMessage(JSON.parse(storedConsent) as { adminConsent?: boolean; state?: string | null })
      } catch {
        // Ignore malformed localStorage fallback values.
      }
      localStorage.removeItem('tc_admin_consent')
      localStorage.removeItem('tc_admin_consent_callback_utc')
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key !== 'tc_admin_consent' || !event.newValue) return
      try {
        handleConsentMessage(JSON.parse(event.newValue) as { adminConsent?: boolean; state?: string | null })
        localStorage.removeItem('tc_admin_consent')
      } catch {
        // ignore malformed
      }
    }

    window.addEventListener('message', onMessage)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener('message', onMessage)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  React.useEffect(() => {
    if (!isMspPlan || activeStep !== 3 || !adminToken || mspConsentMetadataLoaded) {
      return
    }

    let isMounted = true

    const loadMSPConsentMetadata = async () => {
      try {
        const result = await verifyMSPProvisionerConsent(tenantId, adminToken)
        if (!isMounted) {
          return
        }

        setMspProvisionerAppId(result.provisionerAppId || null)
        setMspRoleTargetAppId(result.roleTargetAppId || null)
        setMspProvisionerConsented(result.provisioner)
        setMspRoleTargetConsented(result.roleTarget)
        setMspConsentMetadataLoaded(true)
      } catch {
        if (isMounted) {
          setMspConsentMetadataLoaded(true)
        }
      }
    }

    void loadMSPConsentMetadata()

    return () => {
      isMounted = false
    }
  }, [activeStep, adminToken, isMspPlan, mspConsentMetadataLoaded, tenantId])

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
    setError(null)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
    setError(null)
  }

  const handleReset = () => {
    setActiveStep(0)
    setCertificateData(null)
    setAdminToken('')
    setAuthenticatedTenantId('')
    setAuthenticatedTenantDomain('')
    setAuthenticatedUserName('')
    setAppId(null)
    setConsentUrl(null)
    setConsentPageOpened(false)
    setError(null)
    setMspProvisionerAppId(null)
    setMspRoleTargetAppId(null)
    setMspProvisionerConsented(false)
    setMspRoleTargetConsented(false)
    setMspConsentMetadataLoaded(false)
  }

  const buildConsentUrl = (clientId: string, state?: string) => {
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: `${window.location.origin}/consent-callback`,
    })

    if (state) {
      params.set('state', state)
    }

    return `https://login.microsoftonline.com/${authenticatedTenantId}/adminconsent?${params.toString()}`
  }

  // Always use backend verification as the source of truth for consent chips.
  const refreshMspConsentStatus = React.useCallback(async () => {
    if (!adminToken) {
      throw new Error('Admin token is required. Please sign in to Microsoft 365 again.')
    }

    const result = await verifyMSPProvisionerConsent(tenantId, adminToken)

    if (result.provisionerAppId) setMspProvisionerAppId(result.provisionerAppId)
    if (result.roleTargetAppId) setMspRoleTargetAppId(result.roleTargetAppId)

    setMspProvisionerConsented(result.provisioner)
    setMspRoleTargetConsented(result.roleTarget)

    return result
  }, [adminToken, tenantId])

  const pollMspConsentStatus = React.useCallback(async (attempts = 5, delayMs = 1500) => {
    let lastResult: Awaited<ReturnType<typeof refreshMspConsentStatus>> | null = null

    for (let attempt = 0; attempt < attempts; attempt++) {
      try {
        const result = await refreshMspConsentStatus()
        lastResult = result

        if (result.allConsented) {
          return result
        }
      } catch {
        // Keep retry loop non-blocking for transient popup/graph propagation timing.
      }

      if (attempt < attempts - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs))
      }
    }

    return lastResult
  }, [refreshMspConsentStatus])

  React.useEffect(() => {
    if (!isMspPlan || activeStep !== 3 || !awaitingMspConsentReturn) {
      return
    }

    const onFocusOrVisible = () => {
      if (document.visibilityState !== 'visible') {
        return
      }

      void (async () => {
        try {
          await pollMspConsentStatus(6, 1500)
        } catch {
          // Keep UX non-blocking; explicit Verify button still shows detailed error.
        } finally {
          setAwaitingMspConsentReturn(false)
        }
      })()
    }

    window.addEventListener('focus', onFocusOrVisible)
    document.addEventListener('visibilitychange', onFocusOrVisible)

    return () => {
      window.removeEventListener('focus', onFocusOrVisible)
      document.removeEventListener('visibilitychange', onFocusOrVisible)
    }
  }, [activeStep, awaitingMspConsentReturn, isMspPlan, pollMspConsentStatus])

  // Sign in to Microsoft 365 tenant
  const handleM365SignIn = async () => {
    setLoading(true)
    setError(null)

    try {
      const loginResponse = await instance.loginPopup({
        scopes: [
          'https://graph.microsoft.com/Application.ReadWrite.All',
          'https://graph.microsoft.com/Directory.Read.All',
        ],
        prompt: 'select_account',
      })

      // Extract tenant information from the response
      const tenantId = loginResponse.tenantId
      const accessToken = loginResponse.accessToken
      const userEmail = loginResponse.account?.username || ''
      
      // Get tenant domain from the email or account info
      const domain = loginResponse.account?.tenantId ? 
        (userEmail.split('@')[1] || 'your-tenant.onmicrosoft.com') : 
        'your-tenant.onmicrosoft.com'

      setAuthenticatedTenantId(tenantId)
      setAuthenticatedTenantDomain(domain)
      setAuthenticatedUserName(userEmail)
      setAdminToken(accessToken)

      console.log('Authenticated successfully:', {
        tenantId,
        domain,
        user: userEmail,
      })
    } catch (err: any) {
      console.error('Authentication failed:', err)
      setError(err.message || 'Failed to authenticate. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  // Step 1: Generate Certificate
  const handleGenerateCertificate = async () => {
    setLoading(true)
    setError(null)

    try {
      // Use authenticated domain if available, otherwise fall back to prop
      const domainToUse = authenticatedTenantDomain || tenantDomain
      const result = await generateCertificate(tenantId, domainToUse)
      setCertificateData(result)

      // Trigger download of .cer file
      downloadFile(
        result.cerBase64,
        `${domainToUse}-certificate.cer`,
        'application/x-x509-ca-cert'
      )

      handleNext()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate certificate')
    } finally {
      setLoading(false)
    }
  }

  // Step 2: Create App Registration
  const handleCreateAppRegistration = async () => {
    if (!adminToken || !certificateData) {
      setError('Admin token and certificate data are required')
      return
    }

    if (!authenticatedTenantId) {
      setError('Authenticated tenant ID is missing. Please sign in to Microsoft 365 again.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      console.log('Creating app registration with:', {
        tenantId,
        authenticatedTenantId,
        hasToken: !!adminToken,
        hasCert: !!certificateData.cerBase64
      })

      const result = await createAppRegistration(
        tenantId,
        adminToken,
        certificateData.cerBase64,
        window.location.origin
      )

      console.log('App registration result:', result)

      // Some backend deployments currently return { error: "" } on success.
      // Fall back to certificate status, which is updated with the app ID.
      let createdAppId: string | undefined = result.appId
      if (!createdAppId) {
        const status = await getCertificateStatus(tenantId)
        createdAppId = status.appId
        console.log('Create app registration fallback status:', status)
      }

      if (!createdAppId) {
        throw new Error('App registration created but no App ID was returned')
      }

      setAppId(createdAppId)

      // Generate admin consent URL using authenticated tenant ID
      const consentUrl = buildConsentUrl(createdAppId, 'customer-app-registration-consent')
      setConsentUrl(consentUrl)

      console.log('Generated consent URL:', consentUrl)

      // DO NOT advance to next step - stay on Step 2 to show admin consent UI
      // User will grant consent and then click "Verify Admin Consent" button
    } catch (err: any) {
      console.error('App registration error:', err)
      setError(err.response?.data?.error || err.message || 'Failed to create app registration')
    } finally {
      setLoading(false)
    }
  }

  // Step 3: Verify Admin Consent
  const handleVerifyConsent = async () => {
    if (!appId || !adminToken) {
      setError('App ID and admin token are required')
      return
    }

    const verifyUtc = new Date().toISOString()
    const elapsedMs = consentCallbackUtc
      ? Date.parse(verifyUtc) - Date.parse(consentCallbackUtc)
      : null

    console.log(
      '[CertificateWizard] Verify consent requested:',
      {
        verifyUtc,
        consentCallbackUtc,
        elapsedMs,
        appId,
        tenantId,
      }
    )

    setLoading(true)
    setError(null)

    try {
      const result = await verifyAdminConsent(tenantId, appId, adminToken)

      if (result.consented) {
        setConsentCompleted(true)
        handleNext()
      } else {
        setError(result.message || 'Admin consent has not been granted yet')
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to verify admin consent')
    } finally {
      setLoading(false)
    }
  }

  // When consent is completed in the popup, auto-trigger verification (with a short delay)
  // Removed automatic verification to prevent double execution bug
  // React.useEffect(() => {
  //   if (consentCompleted && appId && adminToken) {
  //     const t = setTimeout(() => {
  //       // attempt verification automatically
  //       handleVerifyConsent()
  //     }, 800)
  //     return () => clearTimeout(t)
  //   }
  //   return
  // }, [consentCompleted, appId, adminToken])

  // MSP_STEP: Verify MSP platform app consent (provisioner + role target)
  const handleVerifyMSPConsent = async () => {
    if (!adminToken) {
      setError('Admin token is required. Please sign in to Microsoft 365 again.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const result = await refreshMspConsentStatus()

      if (result.allConsented) {
        handleNext()
      } else {
        const missing = [
          !result.provisioner ? 'MSP Graph Provisioner' : null,
          !result.roleTarget  ? 'MSP Backend API'       : null,
        ].filter(Boolean).join(' and ')
        setError(`Admin consent not yet granted for: ${missing}. Please open the consent page(s) above, grant consent, then click Verify again.`)
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to verify MSP platform consent')
    } finally {
      setLoading(false)
    }
  }

  // Step 4: Upload to Key Vault
  const handleUploadToKeyVault = async () => {
    if (!certificateData) {
      setError('Certificate data is required')
      return
    }

    setLoading(true)
    setError(null)
    setWarning(null)

    try {
      console.log('Starting Key Vault upload for tenant:', tenantId)
      const result = await uploadToKeyVault(
        tenantId,
        certificateData.pfxBase64,
        certificateData.password,
        certificateData.thumbprint
      )
      console.log('Key Vault upload result:', result)

      if (result.success) {
        setWarning(result.warning || null)
        console.log('Upload successful, advancing to next step')
        handleNext()
      } else {
        console.log('Upload failed with error:', result.error)
        setError(result.error || 'Failed to upload certificate to Key Vault')
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload certificate to Key Vault')
    } finally {
      setLoading(false)
    }
  }

  // Step 5: Test Connection
  const handleTestConnection = async () => {
    setLoading(true)
    setError(null)

    try {
      // First check if certificate is configured
      const certStatus = await getCertificateStatus(tenantId)
      
      if (!certStatus.hasCertificate) {
        setError('Certificate not uploaded to Key Vault. Please go back to the "Upload to Key Vault" step and complete it.')
        setLoading(false)
        return
      }

      const result = await testCertificateConnection(tenantId)

      if (result.success) {
        handleNext()
        setTimeout(() => {
          onComplete()
        }, 2000)
      } else {
        setError(result.error || 'Certificate connection test failed')
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to test certificate connection')
    } finally {
      setLoading(false)
    }
  }

  // Helper function to download base64 files
  const downloadFile = (base64: string, filename: string, mimeType: string) => {
    const byteCharacters = atob(base64)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }
    const byteArray = new Uint8Array(byteNumbers)
    const blob = new Blob([byteArray], { type: mimeType })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Sign in to Microsoft 365
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              First, we need to authenticate with your Microsoft 365 tenant to capture your tenant information.
              This will allow us to generate a certificate with the correct domain name.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Microsoft 365 Authentication Required</AlertTitle>
              Sign in with a Global Administrator account from your Microsoft 365 tenant.
              We'll automatically detect your tenant ID, domain, and user information.
            </Alert>

            {!adminToken ? (
              <Button
                variant="contained"
                onClick={handleM365SignIn}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                fullWidth
                size="large"
              >
                {loading ? 'Signing in...' : 'Sign in to Microsoft 365'}
              </Button>
            ) : (
              <>
                <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'success.light' }}>
                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckCircleIcon sx={{ mr: 1, color: 'success.dark' }} />
                    <strong>Authenticated Successfully</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>User:</strong> {authenticatedUserName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Tenant ID:</strong> {authenticatedTenantId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Domain:</strong> {authenticatedTenantDomain}
                  </Typography>
                </Paper>

                <Alert severity="success" sx={{ mb: 2 }}>
                  <AlertTitle>Ready to Continue</AlertTitle>
                  Your tenant information has been captured. Click Next to generate your certificate.
                </Alert>

                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleM365SignIn}
                    disabled={loading}
                    sx={{ flex: 1 }}
                  >
                    Sign in as Different User
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ flex: 1 }}
                  >
                    Next: Generate Certificate
                  </Button>
                </Box>
              </>
            )}
          </Box>
        )

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Generate Certificate
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              We'll generate a self-signed certificate for your tenant using the authenticated domain.
              This certificate will be used to authenticate your app registration with Microsoft Graph API.
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Authenticated Tenant Information
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Domain:</strong> {authenticatedTenantDomain}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Tenant ID:</strong> {authenticatedTenantId}
              </Typography>
            </Paper>

            <List>
              <ListItem>
                <ListItemIcon>
                  <VpnKeyIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="2048-bit RSA Certificate"
                  secondary="Industry-standard encryption for secure authentication"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SecurityIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="2-Year Validity"
                  secondary="Certificate will be valid for 24 months"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <DownloadIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Automatic Download"
                  secondary="Both .pfx (private key) and .cer (public key) files will be downloaded"
                />
              </ListItem>
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>Important</AlertTitle>
              Save the downloaded files securely. The .pfx file contains your private key and should
              be stored safely.
            </Alert>

            <Button
              variant="contained"
              onClick={handleGenerateCertificate}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <VpnKeyIcon />}
              sx={{ mt: 3 }}
              fullWidth
            >
              {loading ? 'Generating Certificate...' : 'Generate Certificate'}
            </Button>
          </Box>
        )

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Create App Registration & Grant Admin Consent
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              We'll create an app registration in your Azure AD tenant with the required API
              permissions, then grant admin consent.
            </Typography>

            {certificateData && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Certificate Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Thumbprint:</strong> {certificateData.thumbprint}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Expiry:</strong> {new Date(certificateData.expiryDate).toLocaleDateString()}
                </Typography>
              </Paper>
            )}

            {!appId ? (
              <>
                <Alert severity="info" sx={{ mb: 3 }}>
                  <AlertTitle>Create App Registration</AlertTitle>
                  We'll create an app registration with minimal permissions and automatically open the admin consent page.
                </Alert>
                <Button
                  variant="contained"
                  onClick={handleCreateAppRegistration}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                  fullWidth
                >
                  {loading ? 'Creating App Registration...' : 'Create App Registration'}
                </Button>
              </>
            ) : (
              <>
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'success.light' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.dark' }} />
                    App Registration Created
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>App ID:</strong> {appId}
                  </Typography>
                </Paper>

                {consentUrl && (
                  <>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <AlertTitle>Grant Admin Consent</AlertTitle>
                      Click below to open the admin consent page. Grant permissions, then <strong>close the tab</strong> and return here to verify.
                    </Alert>

                    <Button
                      variant={consentCompleted ? "outlined" : "contained"}
                      color={consentCompleted ? "success" : "primary"}
                      startIcon={<AssignmentTurnedInIcon />}
                      onClick={() => {
                        console.log('Opening consent URL:', consentUrl)
                        // Reset consent completed flag when opening the consent page
                        setConsentCompleted(false)
                        setConsentCallbackUtc(null)
                        // Open in a new tab for better compatibility
                        window.open(
                          consentUrl,
                          '_blank'
                        )
                        setConsentPageOpened(true)
                      }}
                      disabled={consentCompleted}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      {consentCompleted ? 'Admin Consent Granted ✓' : 'Open Admin Consent Page'}
                    </Button>

                    <Divider sx={{ my: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        After granting consent
                      </Typography>
                    </Divider>

                    <Button
                      variant="contained"
                      onClick={handleVerifyConsent}
                      disabled={loading || !consentPageOpened}
                      startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                      fullWidth
                    >
                      {loading ? 'Verifying Consent...' : 'Verify Admin Consent'}
                    </Button>
                  </>
                )}
              </>
            )}
          </Box>
        )

      case 3:
        // For MSP plans this is the MSP Platform Access step.
        // For non-MSP plans this is the Upload to Key Vault step (UPLOAD_STEP === 3).
        if (isMspPlan) {
          const provisionerConsentUrl = mspProvisionerAppId
            ? buildConsentUrl(mspProvisionerAppId, 'msp-provisioner-consent')
            : null
          const roleTargetConsentUrl = mspRoleTargetAppId
            ? buildConsentUrl(mspRoleTargetAppId, 'msp-role-target-consent')
            : null

          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                MSP Platform Access
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                As an MSP Client subscriber, our platform needs to run background operations in
                your tenant to provision MSP roles and manage guest access. This requires two
                additional admin consent grants for TC-Config platform apps.
              </Typography>

              <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>Why is this required?</AlertTitle>
                These are <strong>platform-level multi-tenant apps</strong> owned by TC-Config.
                Granting consent creates a service principal in your tenant that our backend
                uses to assign MSP roles — no new app registrations are created in your tenant.
              </Alert>

              {/* App 1: MSP Graph Provisioner */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2">
                    TC-Config MSP Graph Provisioner
                  </Typography>
                  {mspProvisionerConsented
                    ? <Chip label="Consented" color="success" size="small" icon={<CheckCircleIcon />} />
                    : <Chip label="Consent Required" color="warning" size="small" />}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Allows TC-Config to run Graph API operations in your tenant
                  (B2B invite, service principal lookup, role assignment).
                </Typography>
                {mspProvisionerAppId && (
                  <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 1 }}>
                    App ID: {mspProvisionerAppId}
                  </Typography>
                )}
                <Button
                  variant={mspProvisionerConsented ? 'outlined' : 'contained'}
                  color={mspProvisionerConsented ? 'success' : 'primary'}
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  disabled={!provisionerConsentUrl || !authenticatedTenantId}
                  onClick={() => {
                    if (provisionerConsentUrl) {
                      setAwaitingMspConsentReturn(true)
                      window.open(provisionerConsentUrl, '_blank')
                      void pollMspConsentStatus(2, 1200)
                    }
                  }}
                >
                  {mspProvisionerConsented ? 'Consent Granted ✓' : 'Open Consent Page'}
                </Button>
              </Paper>

              {/* App 2: MSP Role Target (SaaS Backend API) */}
              <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2">
                    TC-Config SaaS Backend API
                  </Typography>
                  {mspRoleTargetConsented
                    ? <Chip label="Consented" color="success" size="small" icon={<CheckCircleIcon />} />
                    : <Chip label="Consent Required" color="warning" size="small" />}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Hosts the MSP portal roles (MSPAdmin, MSPUser, etc.) that are assigned to
                  your MSP partner's users in your tenant.
                </Typography>
                {mspRoleTargetAppId && (
                  <Typography variant="caption" color="text.disabled" display="block" sx={{ mb: 1 }}>
                    App ID: {mspRoleTargetAppId}
                  </Typography>
                )}
                <Button
                  variant={mspRoleTargetConsented ? 'outlined' : 'contained'}
                  color={mspRoleTargetConsented ? 'success' : 'primary'}
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  disabled={!roleTargetConsentUrl || !authenticatedTenantId}
                  onClick={() => {
                    if (roleTargetConsentUrl) {
                      setAwaitingMspConsentReturn(true)
                      window.open(roleTargetConsentUrl, '_blank')
                      void pollMspConsentStatus(2, 1200)
                    }
                  }}
                >
                  {mspRoleTargetConsented ? 'Consent Granted ✓' : 'Open Consent Page'}
                </Button>
              </Paper>

              {!authenticatedTenantId && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Tenant ID is not available. Please go back to Step 1 and sign in to Microsoft 365.
                </Alert>
              )}

              {/* Show app IDs when they haven't been fetched yet (first-time) */}
              {!mspConsentMetadataLoaded && authenticatedTenantId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Loading the TC-Config platform app IDs and current consent status.
                </Alert>
              )}

              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  After granting consent for both apps
                </Typography>
              </Divider>

              <Button
                variant="contained"
                onClick={handleVerifyMSPConsent}
                disabled={loading || !adminToken}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                fullWidth
              >
                {loading ? 'Verifying...' : 'Verify & Continue'}
              </Button>
            </Box>
          )
        }

        // Non-MSP: Upload to Key Vault (fall through to case UPLOAD_STEP rendering)
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Upload to Key Vault
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              We'll securely upload your certificate to Azure Key Vault where it will be stored
              encrypted and accessed only by our application.
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <SecurityIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Encrypted Storage"
                  secondary="Certificate stored with military-grade encryption"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <VpnKeyIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Managed Identity Access"
                  secondary="Only our backend service can access your certificate"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary="Automatic Rotation"
                  secondary="Certificate can be renewed without manual intervention"
                />
              </ListItem>
            </List>

            <Alert severity="success" sx={{ mt: 2, mb: 3 }}>
              <AlertTitle>Ready to Upload</AlertTitle>
              Your certificate and app registration are configured. Click below to complete the
              setup.
            </Alert>

            <Button
              variant="contained"
              onClick={handleUploadToKeyVault}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              fullWidth
            >
              {loading ? 'Uploading to Key Vault...' : 'Upload to Key Vault'}
            </Button>
          </Box>
        )

      case 4:
        // MSP plans: Upload to Key Vault (UPLOAD_STEP === 4)
        // Non-MSP plans: Test Connection (TEST_STEP === 4)
        if (isMspPlan) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Upload to Key Vault
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                We'll securely upload your certificate to Azure Key Vault where it will be stored
                encrypted and accessed only by our application.
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Encrypted Storage"
                    secondary="Certificate stored with military-grade encryption"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><VpnKeyIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Managed Identity Access"
                    secondary="Only our backend service can access your certificate"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Automatic Rotation"
                    secondary="Certificate can be renewed without manual intervention"
                  />
                </ListItem>
              </List>

              <Alert severity="success" sx={{ mt: 2, mb: 3 }}>
                <AlertTitle>Ready to Upload</AlertTitle>
                Your certificate and app registration are configured. Click below to complete the setup.
              </Alert>

              <Button
                variant="contained"
                onClick={handleUploadToKeyVault}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                fullWidth
              >
                {loading ? 'Uploading to Key Vault...' : 'Upload to Key Vault'}
              </Button>
            </Box>
          )
        }

        // Non-MSP: Test Connection
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Test Connection
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Let's verify that everything is configured correctly by testing the certificate
              authentication with Microsoft Graph API.
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Retrieve Certificate" secondary="Load certificate from Key Vault" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Authenticate" secondary="Create authenticated Graph API client" />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                <ListItemText primary="Test Query" secondary="Query organization details" />
              </ListItem>
            </List>

            <Button
              variant="contained"
              onClick={handleTestConnection}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
              fullWidth
              sx={{ mt: 2 }}
            >
              {loading ? 'Testing Connection...' : 'Test Connection'}
            </Button>
          </Box>
        )

      case 5:
        // MSP plans: Test Connection (TEST_STEP === 5)
        // Non-MSP plans: Success (SUCCESS_STEP === 5)
        if (isMspPlan) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Test Connection
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Let's verify that everything is configured correctly by testing the certificate
                authentication with Microsoft Graph API.
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Retrieve Certificate" secondary="Load certificate from Key Vault" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Authenticate" secondary="Create authenticated Graph API client" />
                </ListItem>
                <ListItem>
                  <ListItemIcon><CheckCircleIcon color="primary" /></ListItemIcon>
                  <ListItemText primary="Test Query" secondary="Query organization details" />
                </ListItem>
              </List>

              <Button
                variant="contained"
                onClick={handleTestConnection}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                fullWidth
                sx={{ mt: 2 }}
              >
                {loading ? 'Testing Connection...' : 'Test Connection'}
              </Button>
            </Box>
          )
        }

        // Non-MSP: Success
        return (
          <Box textAlign="center">
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Certificate Setup Complete!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your certificate has been successfully configured and tested. Your account is now ready
              to deploy policies to your Microsoft 365 tenant.
            </Typography>

            <Alert severity="success" sx={{ mt: 3 }}>
              <AlertTitle>What's Next?</AlertTitle>
              You can now use the Deployment Wizard to apply policies and branding to your tenant.
            </Alert>
          </Box>
        )

      case 6:
        // MSP plans only: Success (SUCCESS_STEP === 6)
        return (
          <Box textAlign="center">
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Certificate Setup Complete!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your certificate has been successfully configured and tested. Your account is now ready
              to deploy policies to your Microsoft 365 tenant.
            </Typography>

            <Alert severity="success" sx={{ mt: 3 }}>
              <AlertTitle>What's Next?</AlertTitle>
              You can now use the Deployment Wizard to apply policies and branding to your tenant.
            </Alert>
          </Box>
        )

      default:
        return null
    }
  }

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          <AlertTitle>Error</AlertTitle>
          {error}
        </Alert>
      )}

      {warning && (
        <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setWarning(null)}>
          <AlertTitle>Recovered Delay</AlertTitle>
          {warning}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        {renderStepContent(activeStep)}
      </Paper>

      {activeStep !== SUCCESS_STEP && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button disabled={activeStep === 0 || activeStep === UPLOAD_STEP} onClick={handleBack}>
            Back
          </Button>
          {activeStep === SUCCESS_STEP && (
            <Button onClick={handleReset} variant="outlined">
              Start Over
            </Button>
          )}
        </Box>
      )}

    </Box>
  );
}

export default CertificateWizard;
